import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { ASSET_BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import LikeButton from './LikeButton';
import './Dashboard.css';

interface DuckLog {
  id: number;
  user_id: number;
  ship_name: string;
  location?: string;
  found_date: string;
  image_url?: string;
  notes?: string;
  created_at: string;
  username?: string;
  comment_count?: number;
  like_count?: number;
  isLiked?: boolean;
}

interface LogComment {
  id: number;
  user_id: number;
  content: string;
  created_at: string;
  username?: string;
}

type CommentPanelState = {
  loading: boolean;
  comments: LogComment[];
  error?: string;
};

interface ShipOption {
  id: number;
  name: string;
  description?: string;
}

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [logs, setLogs] = useState<DuckLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedLogs, setExpandedLogs] = useState<Record<number, boolean>>({});
  const [commentPanels, setCommentPanels] = useState<Record<number, CommentPanelState>>({});
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');

  const [shipFilter, setShipFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [hasImage, setHasImage] = useState<'all' | 'with' | 'without'>('all');
  const [hasComments, setHasComments] = useState<'all' | 'with' | 'without'>('all');
  const [onlyFollowing, setOnlyFollowing] = useState(false);
  const [ships, setShips] = useState<ShipOption[]>([]);
  const [shipsLoading, setShipsLoading] = useState(false);
  const [shipsError, setShipsError] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
      });
      if (shipFilter.trim()) {
        params.append('ship', shipFilter.trim());
      }
      if (usernameFilter.trim()) {
        params.append('username', usernameFilter.trim());
      }
      if (fromDate) {
        params.append('fromDate', fromDate);
      }
      if (toDate) {
        params.append('toDate', toDate);
      }
      if (hasImage === 'with') {
        params.append('hasImage', '1');
      } else if (hasImage === 'without') {
        params.append('hasImage', '0');
      }
      if (hasComments === 'with') {
        params.append('hasComments', '1');
      } else if (hasComments === 'without') {
        params.append('hasComments', '0');
      }

      // Wenn nicht angemeldet oder "Nur Gefolgte" nicht aktiviert, öffentliche Logs abrufen
      const basePath = (onlyFollowing && user) ? '/ducks/logs/following' : '/ducks/logs';
      const response = await api.get(`${basePath}?${params.toString()}`);
      const logsData = response.data.logs;
      
      // Like-Status für alle Logs abrufen
      if (user) {
        const likeStatusPromises = logsData.map(async (log: DuckLog) => {
          try {
            const likeResponse = await api.get(`/likes/${log.id}/status`);
            return {
              ...log,
              isLiked: likeResponse.data.isLiked,
              like_count: likeResponse.data.likeCount,
            };
          } catch {
            return {
              ...log,
              isLiked: false,
              like_count: log.like_count || 0,
            };
          }
        });
        const logsWithLikes = await Promise.all(likeStatusPromises);
        setLogs(logsWithLikes);
      } else {
        setLogs(logsData);
      }
      
      setTotalPages(response.data.pagination.totalPages);
    } catch (error: any) {
      console.error('Fehler beim Laden der Logs:', error);
      // Bei 401-Fehler nicht weiterleiten, da Dashboard öffentlich ist
      if (error.response?.status === 401) {
        // Einfach leere Logs anzeigen
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [page, shipFilter, usernameFilter, fromDate, toDate, hasImage, hasComments, onlyFollowing, user]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    // Bei Filter- oder Feed-Änderungen wieder auf Seite 1 springen
    setPage(1);
  }, [shipFilter, usernameFilter, fromDate, toDate, hasImage, hasComments, onlyFollowing]);

  useEffect(() => {
    const loadShips = async () => {
      try {
        setShipsLoading(true);
        setShipsError('');
        const response = await api.get('/ships');
        setShips(response.data.ships ?? []);
      } catch (error: any) {
        setShipsError(error.response?.data?.error || 'Schiffe konnten nicht geladen werden.');
      } finally {
        setShipsLoading(false);
      }
    };
    loadShips();
  }, []);

  const ensureCommentsLoaded = useCallback(
    async (logId: number) => {
      setCommentPanels((prev) => ({
        ...prev,
        [logId]: {
          comments: prev[logId]?.comments ?? [],
          loading: true,
          error: undefined,
        },
      }));
      try {
        const response = await api.get(`/comments/${logId}`);
        setCommentPanels((prev) => ({
          ...prev,
          [logId]: {
            comments: response.data.comments,
            loading: false,
          },
        }));
      } catch (err: any) {
        const message = err?.response?.data?.error || 'Kommentare konnten nicht geladen werden';
        setActionError(message);
        setCommentPanels((prev) => ({
          ...prev,
          [logId]: {
            comments: prev[logId]?.comments ?? [],
            loading: false,
            error: message,
          },
        }));
      }
    },
    []
  );

  const handleToggleComments = (logId: number) => {
    setExpandedLogs((prev) => {
      const willOpen = !prev[logId];
      if (willOpen && !commentPanels[logId]) {
        ensureCommentsLoaded(logId);
      }
      return { ...prev, [logId]: willOpen };
    });
  };

  const handleDeleteLog = async (logId: number) => {
    if (!window.confirm('Fund wirklich löschen? Alle zugehörigen Kommentare werden entfernt.')) {
      return;
    }
    try {
      setActionError('');
      setDeletingLogId(logId);
      await api.delete(`/admin/duck-logs/${logId}`);
      setLogs((prev) => prev.filter((log) => log.id !== logId));
      setCommentPanels((prev) => {
        const next = { ...prev };
        delete next[logId];
        return next;
      });
      setExpandedLogs((prev) => {
        const next = { ...prev };
        delete next[logId];
        return next;
      });
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Fund konnte nicht gelöscht werden');
    } finally {
      setDeletingLogId(null);
    }
  };

  const handleDeleteComment = async (commentId: number, logId: number) => {
    if (!window.confirm('Kommentar wirklich löschen?')) {
      return;
    }
    try {
      setActionError('');
      setDeletingCommentId(commentId);
      await api.delete(`/admin/comments/${commentId}`);
      setCommentPanels((prev) => {
        const existing = prev[logId];
        if (!existing) {
          return prev;
        }
        return {
          ...prev,
          [logId]: {
            ...existing,
            comments: existing.comments.filter((comment) => comment.id !== commentId),
          },
        };
      });
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Kommentar konnte nicht gelöscht werden');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleToggleLike = async (logId: number, currentLiked: boolean) => {
    if (!user) return;
    
    const log = logs.find((l) => l.id === logId);
    if (!log) return;

    const isCurrentlyLiked = currentLiked;
    
    try {
      setActionError('');
      if (isCurrentlyLiked) {
        const response = await api.delete(`/likes/${logId}`);
        setLogs((prev) =>
          prev.map((l) =>
            l.id === logId
              ? { ...l, isLiked: false, like_count: response.data.likeCount }
              : l
          )
        );
      } else {
        const response = await api.post(`/likes/${logId}`);
        setLogs((prev) =>
          prev.map((l) =>
            l.id === logId
              ? { ...l, isLiked: true, like_count: response.data.likeCount }
              : l
          )
        );
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Fehler beim Liken');
    }
  };

  if (loading) {
    return <div className="loading">Lade Logs...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        {!isAuthenticated && (
          <div className="welcome-section">
            <h1>Willkommen bei Cruising Ducks! 🦆</h1>
            <div className="welcome-content">
              <p className="welcome-intro">
                Entdecke die faszinierende Welt der Cruising Ducks! Diese kleinen Enten reisen auf Kreuzfahrtschiffen 
                um die Welt und werden von Passagieren gefunden, geloggt und weitergegeben.
              </p>
              <div className="welcome-features">
                <div className="welcome-feature">
                  <span className="welcome-icon">📸</span>
                  <div>
                    <h3>Fundberichte teilen</h3>
                    <p>Teile deine Entdeckungen mit Fotos und Details über deine Funde</p>
                  </div>
                </div>
                <div className="welcome-feature">
                  <span className="welcome-icon">🚢</span>
                  <div>
                    <h3>Schiffe verfolgen</h3>
                    <p>Sieh, welche Enten auf welchen Schiffen gefunden wurden</p>
                  </div>
                </div>
                <div className="welcome-feature">
                  <span className="welcome-icon">👥</span>
                  <div>
                    <h3>Community</h3>
                    <p>Verbinde dich mit anderen Entenjägern und teile deine Abenteuer</p>
                  </div>
                </div>
                <div className="welcome-feature">
                  <span className="welcome-icon">❤️</span>
                  <div>
                    <h3>Favoriten</h3>
                    <p>Markiere deine Lieblingsfunde und sammle sie in deiner Favoritenliste</p>
                  </div>
                </div>
              </div>
              <div className="welcome-cta">
                <p>Möchtest du mitmachen?</p>
                <div className="welcome-buttons">
                  <Link to="/register" className="button-primary">
                    Jetzt registrieren
                  </Link>
                  <Link to="/login" className="ghost-button">
                    Anmelden
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        {isAuthenticated && (
          <>
            <h1>🦆 Gefundene Enten</h1>
            <p className="dashboard-subtitle">
              Entdecke die neuesten Fundberichte der Cruising Ducks Community
            </p>
          </>
        )}
      </div>
      {isAuthenticated && user && (
        <div className="dashboard-feed-toggle">
        <button
          type="button"
          className={!onlyFollowing ? 'chip-button active' : 'chip-button'}
          onClick={() => setOnlyFollowing(false)}
        >
          Alle Funde
        </button>
        <button
          type="button"
          className={onlyFollowing ? 'chip-button active' : 'chip-button'}
          onClick={() => setOnlyFollowing(true)}
        >
          Nur von Nutzern, denen ich folge
        </button>
      </div>
      )}
      <div className="dashboard-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="shipFilter">Schiff</label>
            <select
              id="shipFilter"
              value={shipFilter}
              onChange={(e) => setShipFilter(e.target.value)}
              disabled={shipsLoading}
            >
              <option value="">
                {shipsLoading ? 'Schiffe werden geladen…' : 'Alle Schiffe'}
              </option>
              {ships.map((ship) => (
                <option key={ship.id} value={ship.name}>
                  {ship.name}
                </option>
              ))}
            </select>
            {shipsError && <p className="filter-hint error">{shipsError}</p>}
          </div>
          <div className="filter-group">
            <label htmlFor="userFilter">Benutzer</label>
            <input
              id="userFilter"
              type="text"
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              placeholder="Benutzername"
            />
          </div>
        </div>
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="fromDate">von Datum</label>
            <input
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="toDate">bis Datum</label>
            <input
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="hasImage">Bild</label>
            <select
              id="hasImage"
              value={hasImage}
              onChange={(e) => setHasImage(e.target.value as any)}
            >
              <option value="all">egal</option>
              <option value="with">nur mit Bild</option>
              <option value="without">nur ohne Bild</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="hasComments">Kommentare</label>
            <select
              id="hasComments"
              value={hasComments}
              onChange={(e) => setHasComments(e.target.value as any)}
            >
              <option value="all">egal</option>
              <option value="with">mit Kommentaren</option>
              <option value="without">ohne Kommentare</option>
            </select>
          </div>
        </div>
      </div>
      {actionError && <div className="dashboard-error">{actionError}</div>}
      <div className="logs-grid">
        {logs.map((log) => (
          <div key={log.id} className="log-card">
            {log.image_url && (
              <img
                src={`${ASSET_BASE_URL}${log.image_url}`}
                alt="Ente"
                className="log-image"
              />
            )}
            <div className="log-content">
              <h3>{log.ship_name}</h3>
              <div className="log-meta">
                {log.location && <span>📍 {log.location}</span>}
                <span>📅 {new Date(log.found_date).toLocaleDateString('de-DE')}</span>
                <span>💬 {log.comment_count ?? 0} Kommentare</span>
                {isAuthenticated && (
                  <LikeButton
                    isLiked={log.isLiked || false}
                    likeCount={log.like_count || 0}
                    onClick={() => handleToggleLike(log.id, log.isLiked || false)}
                    disabled={!user}
                  />
                )}
                {!isAuthenticated && log.like_count !== undefined && (
                  <div className="like-count-display">
                    ❤️ {log.like_count}
                  </div>
                )}
              </div>
              {log.notes && <p className="log-notes">{log.notes}</p>}
              <p className="log-user">
                Geloggt von{' '}
                <Link to={`/profile/${log.user_id}`} className="log-user-link">
                  {log.username || `User #${log.user_id}`}
                </Link>
              </p>
              <Link to={`/log/${log.id}`} className="view-log-link">
                Details ansehen →
              </Link>
              {isAdmin && (
                <div className="admin-actions">
                  <div className="admin-chip">Admin-Tools</div>
                  <div className="admin-buttons">
                    <button
                      type="button"
                      onClick={() => handleToggleComments(log.id)}
                      className="ghost-button"
                    >
                      {expandedLogs[log.id] ? 'Kommentare ausblenden' : 'Kommentare anzeigen'}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => handleDeleteLog(log.id)}
                      disabled={deletingLogId === log.id}
                    >
                      {deletingLogId === log.id ? 'Wird gelöscht…' : 'Fund löschen'}
                    </button>
                  </div>
                  {expandedLogs[log.id] && (
                    <div className="admin-comment-panel">
                      {commentPanels[log.id]?.loading && <p>Kommentare werden geladen…</p>}
                      {commentPanels[log.id]?.error && (
                        <p className="admin-error-text">{commentPanels[log.id]?.error}</p>
                      )}
                      {(!commentPanels[log.id] || commentPanels[log.id]?.comments.length === 0) &&
                        !commentPanels[log.id]?.loading && (
                          <p className="admin-empty">Keine Kommentare zu diesem Fund.</p>
                        )}
                      {commentPanels[log.id]?.comments.map((comment) => (
                        <div key={comment.id} className="admin-comment">
                          <div className="admin-comment-header">
                            <strong>{comment.username ?? `User #${comment.user_id}`}</strong>
                            <span>{new Date(comment.created_at).toLocaleString('de-DE')}</span>
                          </div>
                          <p>{comment.content}</p>
                          <button
                            type="button"
                            className="danger ghost"
                            onClick={() => handleDeleteComment(comment.id, log.id)}
                            disabled={deletingCommentId === comment.id}
                          >
                            {deletingCommentId === comment.id ? '…' : 'Kommentar löschen'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {logs.length === 0 && (
        <div className="no-logs">
          <p>Noch keine Enten geloggt. Sei der Erste!</p>
          <Link to="/log-duck" className="button-primary">
            Erste Ente loggen
          </Link>
        </div>
      )}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Zurück
          </button>
          <span>Seite {page} von {totalPages}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

